import type { TourOutcome } from "@keydials/api-client";

/** The first-run walkthrough of a dial (KEY-535). Matches the server's `KnownTours.FirstRunDial`. */
export const FIRST_RUN_TOUR_ID = "first-run-dial";

/**
 * Below this the tour is not offered unprompted.
 *
 * This number was chosen for one specific reason, and that reason has since gone: the header's Help button
 * carried `kd-hide-sm` and so was `display: none` below exactly this width, leaving no Help entry point on
 * screen at all — step 6 would have pointed at nothing, and nobody could have found their way back to the
 * tour afterwards. The button now collapses to its icon instead of disappearing, so Help is reachable at
 * every width and that objection no longer holds.
 *
 * The gate is kept anyway, and deliberately not relaxed in the same change that removed its cause. What is
 * left is a question nobody has answered yet rather than a known break: the coach-mark card is 328px wide
 * against a `calc(100vw - 16px)` cap, the dial column and its panel stack at 920px, and no step of the tour
 * has been watched running at phone width. Lowering this is a few minutes of looking at it on a real narrow
 * viewport — not a code change — and should be its own piece of work, done with eyes on the screen.
 */
export const TOUR_MIN_VIEWPORT_PX = 560;

export interface TourOfferInput {
  /** Whether the server has actually answered "has this person seen it?". Never act before it has. */
  loaded: boolean;
  /** Whether they have — a completion or a cancel, both of which mean don't. */
  seen: boolean;
  /** Whether the dial has finished loading, so the kind of dial (and so which steps apply) is known. */
  dialReady: boolean;
  /** A PIN or guest session. Never auto-offered; they can still start the tour from the Help drawer. */
  confined: boolean;
  /** Whether some other guide is already running. */
  guideRunning: boolean;
  /**
   * Whether a modal is open. This matters far more than it looks, because there is no backfill: every
   * existing user meets the tour for the first time on their next dial load, and `useWhatsNew` auto-opens
   * over every app load where anything is unread. Two overlapping overlays on the same first visit is the
   * worst version of this feature.
   */
  modalOpen: boolean;
  viewportWidth: number;
}

/**
 * Whether to offer the first-run walkthrough right now (KEY-535).
 *
 * Every clause is a veto, and the default is silence: an unprompted overlay is the rudest thing this app
 * does, so it happens only when the whole conjunction holds. Pure so it can be unit-tested — the alternative
 * is a seven-clause `if` buried in a provider, which is precisely where a stray `||` would go unnoticed.
 */
export function shouldOfferTour(input: TourOfferInput): boolean {
  return (
    input.loaded &&
    !input.seen &&
    input.dialReady &&
    !input.confined &&
    !input.guideRunning &&
    !input.modalOpen &&
    input.viewportWidth >= TOUR_MIN_VIEWPORT_PX
  );
}

/** Why a running guide stopped. */
export type TourEndReason = "stopped" | "finished";

/**
 * What to record when a guide ends, or null when there is nothing to record.
 *
 * Two things live in here. First, only the first-run tour has server-side state, so ending any other guide
 * writes nothing. Second — and this is the whole of "keep it light, allow cancel" — a cancel is recorded
 * just as firmly as a completion. If it weren't, ✕ would mean "ask me again on the next dial", which is the
 * opposite of what dismissing something means.
 */
export function tourOutcomeToRecord(guideId: string | null | undefined, reason: TourEndReason): TourOutcome | null {
  if (guideId !== FIRST_RUN_TOUR_ID) {
    return null;
  }
  return reason === "finished" ? "Completed" : "Cancelled";
}

/**
 * Whether a modal is currently open over the page.
 *
 * `aria-modal="true"` is a precise probe rather than a guess: `Modal` and `SlideOver` both set it, and the
 * help overlay's own coach-mark card deliberately does not (it is a `role="dialog"` the page stays
 * interactive behind, which is the point of the spotlight).
 */
export function isModalOpen(doc: Pick<Document, "querySelector"> = document): boolean {
  return doc.querySelector('[aria-modal="true"]') !== null;
}
